/**
 * PATH: packages/tenant-shared/services/deepseek.ts
 * PURPOSE: Typed browser client for backend-proxied AI endpoints.
 */

import type {
  ScheduleAIReviewRun,
  ScheduleAIReviewRunListItem,
  ScheduleDraftResponse,
} from '@quotech/job-hub/schedule/types'
import { getAccessToken } from '../hooks/useApi'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

export interface AIMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface AIChatResponse {
  content: string
  model_used: string
}

export interface JobIntakeExtraction {
  summary: string
  job_number: string | null
  client_id: number | null
  client_name: string | null
  site_address: string | null
  scope: string | null
  contract_value: string | null
  quote_estimated_value: string | null
  status: string | null
  win_status: string | null
  stage: string | null
  start_date: string | null
  est_completion: string | null
  notes: string | null
  confidence: number | null
}

export interface InvoiceIntakeLineExtraction {
  description: string
  amount: string
}

export interface InvoiceIntakeExtraction {
  summary: string
  job_id: number | null
  job_number: string | null
  client_name: string | null
  invoice_number: string | null
  type: string | null
  amount_ex_gst: string | null
  date_issued: string | null
  date_due: string | null
  notes: string | null
  lines: InvoiceIntakeLineExtraction[]
  confidence: number | null
}

export interface QuoteExportPolishResult {
  summary: string
  intro_text: string
  scope_text: string
  programme_text: string
  inclusions: string[]
  exclusions: string[]
  notes: string[]
  acceptance_text: string
}

export interface QuoteTrackerFollowUp {
  task: string
  assigned_to: string | null
  assigned_employee_id: number | null
  start_date: string | null
  end_date: string | null
  status: string
  notes: string | null
}

export interface QuoteTrackerExtraction {
  summary: string
  quote_number: string | null
  job_id: number | null
  job_number: string | null
  client_id: number | null
  client_name: string | null
  job_description: string | null
  source_bucket: string | null
  status: string | null
  outcome_status: string | null
  assigned_to: string | null
  review_owner: string | null
  priority_code: string | null
  tracker_marker: string | null
  contact_name: string | null
  contact_phone: string | null
  date_issued: string | null
  due_date: string | null
  chased_date: string | null
  odds_pct: number | null
  estimating_hours: string | null
  total_ex_gst: string | null
  tracker_notes: string | null
  approval_status: string | null
  approval_notes: string | null
  client_approved: boolean | null
  client_approval_reference: string | null
  follow_up_items: QuoteTrackerFollowUp[]
  confidence: number | null
}

export interface AIApplyRunResult {
  run_id: number
  applied_actions: number
  skipped_actions: number
  failed_actions: number
  status: string
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    try {
      const parsed = JSON.parse(text) as { detail?: string }
      throw new Error(parsed.detail || text || `AI request failed with status ${response.status}`)
    } catch {
      throw new Error(text || `AI request failed with status ${response.status}`)
    }
  }
  return response.json() as Promise<T>
}

export async function chatCompletion(messages: AIMessage[], options?: {
  model?: string
  temperature?: number
  maxTokens?: number
}): Promise<AIChatResponse> {
  const response = await fetch(`${API_BASE}/ai/chat`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      messages,
      model: options?.model,
      temperature: options?.temperature ?? 0.4,
      max_tokens: options?.maxTokens ?? 1800,
    }),
  })

  return parseResponse<AIChatResponse>(response)
}

export async function draftOpsTimeline(notes: string, today?: string): Promise<ScheduleDraftResponse> {
  const response = await fetch(`${API_BASE}/ai/ops/timeline-draft`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      notes,
      today,
    }),
  })

  return parseResponse<ScheduleDraftResponse>(response)
}

export async function createAiReviewRun(notes: string, today?: string): Promise<ScheduleAIReviewRun> {
  const response = await fetch(`${API_BASE}/ai/ops/review-runs`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ notes, today }),
  })

  return parseResponse<ScheduleAIReviewRun>(response)
}

export async function listAiReviewRuns(limit = 20): Promise<ScheduleAIReviewRunListItem[]> {
  const response = await fetch(`${API_BASE}/ai/ops/review-runs?limit=${limit}`, {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  })

  return parseResponse<ScheduleAIReviewRunListItem[]>(response)
}

export async function getAiReviewRun(runId: number): Promise<ScheduleAIReviewRun> {
  const response = await fetch(`${API_BASE}/ai/ops/review-runs/${runId}`, {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'include',
  })

  return parseResponse<ScheduleAIReviewRun>(response)
}

export async function updateAiActionApproval(actionId: number, approvalStatus: 'approved' | 'rejected'): Promise<ScheduleAIReviewRun> {
  const response = await fetch(`${API_BASE}/ai/ops/actions/${actionId}/approval`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ approval_status: approvalStatus }),
  })

  return parseResponse<ScheduleAIReviewRun>(response)
}

export async function applyAiReviewRun(runId: number): Promise<AIApplyRunResult> {
  const response = await fetch(`${API_BASE}/ai/ops/review-runs/${runId}/apply`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
  })

  return parseResponse<AIApplyRunResult>(response)
}

export async function deleteAiReviewRun(runId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/ai/ops/review-runs/${runId}`, {
    method: 'DELETE',
    headers: authHeaders(),
    credentials: 'include',
  })
  if (!response.ok && response.status !== 204) {
    const text = await response.text().catch(() => '')
    try {
      const parsed = JSON.parse(text) as { detail?: string }
      throw new Error(parsed.detail || text || `AI review run delete failed (${response.status})`)
    } catch {
      throw new Error(text || `AI review run delete failed (${response.status})`)
    }
  }
}

export async function extractJobIntake(rawText: string): Promise<JobIntakeExtraction> {
  const response = await fetch(`${API_BASE}/ai/extract/job-intake`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ raw_text: rawText }),
  })

  return parseResponse<JobIntakeExtraction>(response)
}

export async function extractInvoiceIntake(rawText: string, jobId?: number): Promise<InvoiceIntakeExtraction> {
  const response = await fetch(`${API_BASE}/ai/extract/invoice-intake`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      raw_text: rawText,
      job_id: jobId ?? null,
    }),
  })

  return parseResponse<InvoiceIntakeExtraction>(response)
}

export async function extractQuoteTracker(rawText: string, jobId?: number): Promise<QuoteTrackerExtraction> {
  const response = await fetch(`${API_BASE}/ai/extract/quote-tracker`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      raw_text: rawText,
      job_id: jobId ?? null,
    }),
  })

  return parseResponse<QuoteTrackerExtraction>(response)
}

// ---------------------------------------------------------------------------
// Email AI classification & entity extraction
// ---------------------------------------------------------------------------

export interface EmailExtractedEntity {
  entity_type: string
  value: string
  label: string | null
  job_id: number | null
  client_id: number | null
  confidence: number | null
}

export interface EmailSuggestedAction {
  action: string
  label: string
  description: string | null
  priority: string
  payload: Record<string, unknown>
}

export interface EmailClassifyResult {
  category: string
  category_label: string
  confidence: number
  summary: string
  sentiment: string | null
  urgency: string
  entities: EmailExtractedEntity[]
  suggested_actions: EmailSuggestedAction[]
  key_dates: string[]
  key_amounts: string[]
  suggested_reply: string | null
}

export interface EmailDraftReplyResult {
  subject: string
  body: string
  confidence: number
}

export async function classifyEmail(email: {
  subject: string
  body_text: string
  from_address: string
  from_name: string
  /** Pass-through to the backend AI classifier. Accepts either a
   *  list of bare email addresses or `{name, email}` recipient
   *  objects (the actual mailbox payload shape from Outlook/Gmail). */
  to_addresses?: Array<string | { name?: string; email: string; address?: string }>
}): Promise<EmailClassifyResult> {
  const response = await fetch(`${API_BASE}/ai/email/classify`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify(email),
  })
  return parseResponse<EmailClassifyResult>(response)
}

/** Email draft assistant modes.
 *  - `reply`    : draft a reply to an incoming email (the original behaviour).
 *  - `compose`  : draft a new email from the writer's brief (`body_text`).
 *  - `rewrite` / `shorten` / `tone` / `grammar`: transform a highlighted
 *    `selection` and return only the revised text in `body`.
 *  Older tenant backends ignore the extra `mode`/`selection` keys and behave
 *  as `reply`, so callers degrade gracefully until the BE redeploys. */
export type EmailDraftMode =
  | 'reply'
  | 'compose'
  | 'rewrite'
  | 'shorten'
  | 'tone'
  | 'grammar'

export async function draftEmailReply(params: {
  subject: string
  body_text: string
  from_name: string
  context?: string
  tone?: string
  mode?: EmailDraftMode
  selection?: string
}): Promise<EmailDraftReplyResult> {
  const response = await fetch(`${API_BASE}/ai/email/draft-reply`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify(params),
  })
  return parseResponse<EmailDraftReplyResult>(response)
}

export async function polishQuoteExport(draft: unknown, totals: unknown): Promise<QuoteExportPolishResult> {
  const response = await fetch(`${API_BASE}/ai/quotes/polish-export`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ draft, totals }),
  })

  return parseResponse<QuoteExportPolishResult>(response)
}
